import type { Difficulty } from './types';

export type NarrationSelectionStep = 'mode' | 'opponents' | 'intro';

export function opponentIntroNarrationKey(
  arenaActive: boolean,
  selectionStep: NarrationSelectionStep,
  difficulty: Difficulty,
): Difficulty | null {
  return !arenaActive && selectionStep === 'intro' ? difficulty : null;
}

export const NARRATOR_INTROS: Record<Difficulty, string> = {
  beginner: '山腳的竹院裡，住著一個總在替別人收棋的少年，阿芽。他把旁觀過的每一局都抄進舊譜，卻從沒有人真正把他當成棋手。三年前，送譜上山的父親沒有回來，只在最後一頁留下半幅棋局，和一句「看清下一步」。今天，當你帶著無名棋匣走進竹院，他終於鼓起勇氣，要用自己的名字，邀你下第一局。',
  trader: '市橋最熱鬧的地方，鐵算盤留了一張不收錢的棋桌。他曾替欠債的街坊作保，保住了別人的屋瓦，卻失去了自己的家。從那以後，他相信棋盤上沒有白拿的便宜，每一次交換，都必須算清代價。只是有一筆帳，他始終不敢重算：當年的選擇，究竟值不值得。想過橋，就陪他把這一局算完。',
  cannonhorse: '兩聲號響，一聲催馬，一聲試炮。雷駒曾是雲城最快的驛使，卻有一封告別信，遲到了一個時辰。收信的人早已不在，他仍把信壓在棋盤底下，日日練著馬的轉折與炮的長線，彷彿只要再快一點，遲來的承諾就還能抵達。今天，他要把這封信交給能追上他棋路的人。',
  gambiteer: '赤灘上還留著焦木的氣味。赤刃曾親手燒掉水軍最後一艘渡船，逼退無可退的士卒殺出重圍。史書只記奇勝，他卻記得船上原本替傷兵留的位置。從此，他在棋盤上反覆追問：犧牲究竟是勇氣，還是最容易被勝利原諒的錯誤。',
  attacker: '風垣的舊城門前，殘旗仍被烈風吹得作響。烈風曾追破敵軍，贏了戰場，卻因越過軍令的界線，來不及回援故城。眾人稱他破陣將軍，他記得的卻是身後那扇關不上的門。於是他把所有未竟的衝鋒留在棋盤上，等一個人告訴他：進攻，是否也能成為一種守護。',
  defender: '雨關那一夜，玄壁沒有守住城門，卻讓門後的百姓全數平安撤離。戰報只寫下「守將棄關」，沒有人替他補上後半句。他從不辯解，只把當夜每一道防線，一次又一次重現在棋盤上。山雨封道的今天，他替你移開路障，再把棋擺好。若你能找到牆上的門，也許就能看懂他為何選擇後退。',
  pawn: '青穗不是將軍，只是替雲城守田的人。兵亂斷糧時，她帶著五戶人家沿河復耕，把一袋種子變成全城的冬糧。失散的弟弟至今沒有生死，她仍每天多留一碗飯。她相信小兵走得雖慢，只要每一步都留下根，就能把最遠的歸路接回家。',
  miser: '藏樓裡，千百枚古棋安靜得像在聽。金庫相信，每一顆殘子背後，都有一個不能被遺忘的名字。最深處那枚燒焦的紅兵，是他從故鄉廢墟裡拾回的唯一物件。收藏得越多，他卻越分不清保存與囚禁的界線。這一局，他請你選出最捨不得失去的棋子，再證明你知道何時保留，何時讓它完成使命。',
  blockader: '無渡熟悉江上的每一道暗流。兵亂時，他沉下鐵鏈與舊舟，沒有擊沉敵人，卻讓整條江無路可走。那道封鎖救了上游，也斷了下游的退路。多年來他仍在霧裡等一艘沒有被打撈的船。想過他的棋盤，不能只會撞牆，還得讓水找到出口。',
  scholar: '墨閣裡藏著百卷殘譜，先生只看第一步，就能說出它的年代與師承。年少時，他曾替名家完成巨著，換來母親的醫藥，也換來永不署名的承諾。如今世人背誦他的招法，卻不知道他的名字。這一局，他要等你走到譜盡之處，再寫出真正屬於自己的下一步。',
  veteran: '殘亭只亮一盞秋燈，桌上放著一盤多年未完的棋。那是老人弟子的殘局；少年當年笑說明日再下，卻再也沒有回亭。秋燈守著那一步，不是等答案，而是在學著接受，有些棋局永遠不會結束。他不問你從哪裡來，只問你是否願意安靜地，把眼前每一步走到底。',
  master: '十二段舊刻在無名棋匣底合成一句話，雲門終於打開。門後的雲隱，年少時曾走遍各地求一敗，也曾用這只棋匣收藏所有勝局。直到有一天，他發現自己記得每一次勝利，卻不記得任何敗者後來去了哪裡。於是，他把棋匣送下山，把最後一個問題留給未來的持匣人：你能否超越自己最熟悉的那一步？',
};
